import { Suspense } from "react"
import { node } from "prop-types"
import { Link, Navigate, useLocation } from "react-router-dom"
import { Loading } from "source/shared/components"
import OpenGraphTags from "source/shared/OpenGraphTags"
import { useQRCode } from "source/shared/QRCode"
import { useApiRead } from "source/shared/SessionApiResource"
import BlankState from "source/groups/BlankState"
import { BreadcrumbNav } from "./components/BreadcrumbNav"
import { GroupList } from "./components/GroupList"
import { Filters } from "./components/Filters"
import { FiltersContextProvider } from "./contexts/FiltersContext"
import { MapContextProvider } from "./contexts/MapContext"
import { useGroupType } from "./hooks/useGroupType"
import { Grid } from "@planningcenter/doxy-web"

export default function Entry() {
  useQRCode()

  return (
    <Suspense fallback={<Loading />}>
      <GroupType />
    </Suspense>
  )
}

function GroupType() {
  const { data, errors } = useGroupType()
  const { name, description } = data.attributes

  const {
    meta: { total_count: totalGroupTypeCount },
  } = useApiRead("/groups/v2/group_types?per_page=0")

  if (errors) {
    return (
      <BlankState icon="churchCenter#groups-o">
        <p className="mb-2">This group type is currently unavailable</p>
        <Link to="/groups" className="mr-1 minor-btn secondary-btn btn">
          View all groups
        </Link>
      </BlankState>
    )
  }

  return (
    <RedirectIfHistoricalSlug>
      <OpenGraphTags title={name} description={description} />
      {totalGroupTypeCount > 1 && <BreadcrumbNav />}
      {description && <Description>{description}</Description>}
      <FiltersContextProvider>
        <MapContextProvider>
          <Grid columns={1} gap={3}>
            <Filters />
            <Suspense fallback={<Loading />}>
              <GroupList />
            </Suspense>
          </Grid>
        </MapContextProvider>
      </FiltersContextProvider>
    </RedirectIfHistoricalSlug>
  )
}

function RedirectIfHistoricalSlug({ children }) {
  const {
    data: {
      attributes: { church_center_web_url },
    },
  } = useGroupType()
  const { pathname: actualPath } = useLocation()
  const expectedPath = new URL(church_center_web_url).pathname

  return expectedPath === actualPath ? (
    children
  ) : (
    <Navigate to={expectedPath} replace />
  )
}
RedirectIfHistoricalSlug.propTypes = {
  children: node,
}

function Description({ children }) {
  {
    /* description is sanitized on the vertex */
  }
  return (
    <div
      data-preformatted-content="heading-scale:legacy-church-center"
      className="mb-3"
      dangerouslySetInnerHTML={{ __html: children }}
    />
  )
}
Description.propTypes = {
  children: node,
}
